/**
 * aoc-modal.js
 *
 * Opens/closes the Acknowledgement of Country modal (footer flag trigger).
 * Uses the same a11y-modal shell and animation pattern as accessibility-modal.js.
 *
 * Relies on globals from global.js:
 *   focusTrap(element), releaseFocusTrap(), announceToScreenReader(message), prefersReducedMotion()
 */

class AocModal {
  constructor() {
    this.modal = document.getElementById('aoc-modal');
    this.trigger = document.getElementById('aoc-trigger');
    this.closeBtn = document.getElementById('aoc-modal-close');
    this.isClosing = false;

    if (!this.modal) return;

    this._ensureClosed();
    this._bindEvents();
  }

  _isOpen() {
    return this.modal && !this.modal.hasAttribute('hidden');
  }

  _allowsMotion() {
    return typeof prefersReducedMotion !== 'function' || !prefersReducedMotion();
  }

  _ensureClosed() {
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('hidden', '');
    if (typeof hideModalBackdrop === 'function') {
      hideModalBackdrop();
    }
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
    }
    this.isClosing = false;
  }

  open() {
    if (!this.modal || this._isOpen() || this.isClosing) return;

    this.modal.removeAttribute('hidden');
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    if (this._allowsMotion()) {
      this.modal.classList.remove('is-open');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal.classList.add('is-open');
        });
      });
    } else {
      this.modal.classList.add('is-open');
    }

    if (typeof openModalBackdrop === 'function') {
      openModalBackdrop();
    }

    if (typeof focusTrap === 'function') {
      focusTrap(this.modal);
    }

    if (this.closeBtn) {
      this.closeBtn.focus();
    }

    if (typeof announceToScreenReader === 'function') {
      announceToScreenReader('Acknowledgement of Country opened.');
    }
  }

  close() {
    if (!this.modal || !this._isOpen() || this.isClosing) return;

    if (this._allowsMotion()) {
      this.isClosing = true;
      this.modal.classList.remove('is-open');

      const overlay =
        typeof getModalOverlay === 'function' ? getModalOverlay() : null;
      if (overlay) {
        overlay.classList.remove('is-open');
      }

      let finished = false;
      let pending = overlay ? 2 : 1;

      const complete = () => {
        if (finished) return;
        finished = true;
        if (typeof hideModalBackdrop === 'function') {
          hideModalBackdrop();
        }
        this._finishClose();
      };

      const tryComplete = () => {
        pending -= 1;
        if (pending <= 0) complete();
      };

      const onTransitionEnd = (event) => {
        if (event.target === this.modal && event.propertyName === 'transform') {
          tryComplete();
        }
        if (overlay && event.target === overlay && event.propertyName === 'opacity') {
          tryComplete();
        }
      };

      this.modal.addEventListener('transitionend', onTransitionEnd);
      if (overlay) overlay.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(() => {
        this.modal.removeEventListener('transitionend', onTransitionEnd);
        if (overlay) overlay.removeEventListener('transitionend', onTransitionEnd);
        complete();
      }, MODAL_TRANSITION_FALLBACK_MS);
      return;
    }

    if (typeof hideModalBackdrop === 'function') {
      hideModalBackdrop();
    }
    this._finishClose();
  }

  _finishClose() {
    if (!this.modal || this.modal.hasAttribute('hidden')) return;

    if (typeof hideModalBackdrop === 'function') {
      hideModalBackdrop();
    }

    this.modal.setAttribute('hidden', '');
    this.modal.classList.remove('is-open');
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
    }
    this.isClosing = false;

    if (typeof releaseFocusTrap === 'function') {
      releaseFocusTrap();
    }

    if (this.trigger) {
      this.trigger.focus();
    }

    if (typeof announceToScreenReader === 'function') {
      announceToScreenReader('Acknowledgement of Country closed.');
    }
  }

  _bindEvents() {
    if (this.trigger) {
      this.trigger.addEventListener('click', () => {
        if (this._isOpen()) {
          this.close();
        } else {
          this.open();
        }
      });
    }

    document.addEventListener('click', (event) => {
      if (!this._isOpen()) return;

      if (event.target.closest('[data-aoc-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest('[data-modal-close]')) {
        this.close();
        return;
      }

      if (
        !this.modal.contains(event.target) &&
        !event.target.closest('[data-aoc-open]')
      ) {
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this._isOpen()) {
        event.preventDefault();
        this.close();
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new AocModal();
});
